import Script from 'next/script';
import PropTypes from 'prop-types';

import { sendEvent } from 'lib/gtag';
import { trackEvent } from 'lib/posthog';
import { getFromSessionStorage } from 'lib/sessionStorage';
import { noop, waitForGlobalWindowVar } from 'lib/utils';

import { useUser } from 'providers/UserProvider';

import { postSignIn } from 'services/user';

const renderLoginButton = (gaEventCategory) => {
  const containers = document.querySelectorAll('#google-btn');

  containers.forEach((container) => {
    // to customize, use: https://developers.google.com/identity/gsi/web/tools/configurator
    window.google.accounts.id.renderButton(container, {
      click_listener: () => {
        sendEvent({
          action: 'login_google_submit',
          category: gaEventCategory,
        });
      },
      locale: 'pt-BR',
      logo_alignment: 'center',
      shape: 'square',
      size: 'large',
      text: 'signin',
      theme: 'outline',
      type: 'icon',
    });
  });
};

const GoogleLogin = ({
  gaEventCategory,
  onSuccess = noop,
  onFailure = noop,
  ...rest
}) => {
  const { login } = useUser();

  const onSignInSuccess = async ({ credential }) => {
    let isNewUser = false;

    try {
      const utmCampaign = getFromSessionStorage('utm_campaign');

      const result = await postSignIn({
        oauthDomain: 'google',
        oauthToken: credential,
        utmCampaign,
      });

      isNewUser = result.isNewUser ?? false;
      await login({ token: result.token });

      const gaEventActionPrefix = isNewUser ? 'signup' : 'login';

      sendEvent({
        action: `${gaEventActionPrefix}_google_success`,
        category: gaEventCategory,
      });
      trackEvent(isNewUser ? 'try signup' : 'try login', {
        source_type: 'user',
        status: 'success',
        option: 'google',
      });

      onSuccess();
    } catch (err) {
      const { captureException } = await import('@sentry/nextjs');

      captureException(err);
      trackEvent(isNewUser ? 'try signup' : 'try login', {
        source_type: 'user',
        status: 'error',
        option: 'google',
        message: Number.isInteger(err?.status) ? String(err.status) : 'unknown',
      });
      onFailure(err);
    }
  };

  return (
    <>
      <Script
        id="google-identity-services"
        src="https://accounts.google.com/gsi/client"
        onReady={() => {
          waitForGlobalWindowVar('google', () => {
            window.google.accounts.id.initialize({
              client_id: process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID,
              context: 'signin',
              ux_mode: 'popup',
              auto_prompt: false,
              callback: onSignInSuccess,
            });
            renderLoginButton(gaEventCategory);
          });
        }}
      />
      <div
        className="-ml-3 !max-h-16 w-14 [&_iframe]:!m-0 [&_iframe]:!max-h-16 [&_iframe]:!w-[52px]"
        id="google-btn"
        aria-hidden
        {...rest}
      />
    </>
  );
};

GoogleLogin.propTypes = {
  gaEventCategory: PropTypes.string.isRequired,
  icon: PropTypes.bool,
  onSuccess: PropTypes.func,
  onFailure: PropTypes.func,
};

export default GoogleLogin;
